import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

// STYLE 1: Dark Noir with Rose Gold accents (Aesthetic Medicine - Paris inspired)
const MockupAestheticParis = () => (
  <div className="w-full h-full bg-[#0d0d0d] p-4 overflow-hidden">
    <div className="flex items-center gap-2 mb-3">
      <div className="flex gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-[#2a2a2a]"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-[#2a2a2a]"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-[#2a2a2a]"></div>
      </div>
      <div className="flex-1 bg-white/5 rounded h-5 px-3 flex items-center">
        <span className="text-[8px] text-white/30">institut-beaute-paris.fr</span>
      </div>
    </div>
    <div className="bg-[#0a0a0a] rounded overflow-hidden h-[calc(100%-32px)] border border-[#1a1a1a]">
      <div className="px-4 py-2 flex justify-between items-center border-b border-[#1a1a1a]">
        <span className="text-[10px] tracking-[0.15em] text-[#d4a574]" style={{fontFamily: 'serif'}}>INSTITUT BEAUTÉ</span>
        <div className="flex gap-4">
          <span className="text-[7px] text-white/30">Soins</span>
          <span className="text-[7px] text-white/30">Équipe</span>
          <span className="text-[7px] text-[#d4a574]">Rendez-vous</span>
        </div>
      </div>
      <div className="px-4 py-6 relative">
        <div className="absolute top-0 right-0 w-24 h-24 bg-[radial-gradient(circle_at_center,rgba(212,165,116,0.08)_0%,transparent_70%)]" />
        <p className="text-[7px] tracking-[0.25em] text-[#d4a574]/70 mb-2">MÉDECINE ESTHÉTIQUE</p>
        <p className="text-white/90 text-sm font-light mb-1" style={{fontFamily: 'serif'}}>L'Art de la</p>
        <p className="text-[#d4a574] text-sm font-light mb-4" style={{fontFamily: 'serif'}}>Beauté Naturelle</p>
        <div className="border border-[#d4a574]/40 px-4 py-2 inline-block hover:bg-[#d4a574]/10 transition-colors">
          <span className="text-[7px] text-[#d4a574] tracking-[0.2em]">PRENDRE RDV</span>
        </div>
      </div>
      <div className="px-4 pb-4 grid grid-cols-2 gap-2">
        <div className="bg-[#111] p-2 border-l-2 border-[#d4a574]/30">
          <p className="text-[7px] text-white/80">Injections</p>
          <p className="text-[5px] text-white/30">Botox & Fillers</p>
        </div>
        <div className="bg-[#111] p-2 border-l-2 border-[#d4a574]/30">
          <p className="text-[7px] text-white/80">Rajeunissement</p>
          <p className="text-[5px] text-white/30">Lifting sans chirurgie</p>
        </div>
      </div>
    </div>
  </div>
);

// STYLE 2: Dark Emerald & Silver (Plastic Surgery - London/Harley Street inspired)
const MockupPlasticLondon = () => (
  <div className="w-full h-full bg-[#0a0f0d] p-4 overflow-hidden">
    <div className="flex items-center gap-2 mb-3">
      <div className="flex gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-[#1a2a20]"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-[#1a2a20]"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-[#1a2a20]"></div>
      </div>
      <div className="flex-1 bg-white/5 rounded h-5 px-3 flex items-center">
        <span className="text-[8px] text-white/30">harley-aesthetic.co.uk</span>
      </div>
    </div>
    <div className="bg-[#080c0a] rounded overflow-hidden h-[calc(100%-32px)] border border-[#1a2a20]">
      <div className="px-4 py-2 flex justify-between items-center border-b border-[#1a2a20]">
        <span className="text-[10px] text-white/90" style={{fontFamily: 'serif'}}>
          Mr. <span className="text-[#7fa891]">Harrison</span>
        </span>
        <div className="flex gap-4">
          <span className="text-[7px] text-white/30">Procedures</span>
          <span className="text-[7px] text-white/30">Results</span>
          <span className="text-[7px] text-[#7fa891]">Book</span>
        </div>
      </div>
      <div className="px-4 py-5 relative">
        <div className="absolute top-2 right-2 w-20 h-20 bg-[radial-gradient(circle_at_center,rgba(127,168,145,0.1)_0%,transparent_70%)]" />
        <p className="text-[7px] tracking-[0.2em] text-[#7fa891]/80 mb-2">FACIAL PLASTIC SURGERY</p>
        <p className="text-white/90 text-sm font-light mb-1" style={{fontFamily: 'serif'}}>Refined Results,</p>
        <p className="text-[#7fa891] text-sm font-light mb-4" style={{fontFamily: 'serif'}}>Natural Beauty</p>
        <div className="flex gap-2">
          <div className="bg-[#7fa891] px-3 py-1.5">
            <span className="text-[7px] text-[#080c0a] tracking-wider font-medium">VIEW GALLERY</span>
          </div>
          <div className="border border-[#7fa891]/30 px-3 py-1.5">
            <span className="text-[7px] text-[#7fa891]/80 tracking-wider">ENQUIRE</span>
          </div>
        </div>
      </div>
      <div className="px-4 pb-3 grid grid-cols-3 gap-1">
        <div className="text-center p-2 bg-[#0d1310] border border-[#1a2a20]">
          <p className="text-[6px] text-[#7fa891]">Rhinoplasty</p>
        </div>
        <div className="text-center p-2 bg-[#0d1310] border border-[#1a2a20]">
          <p className="text-[6px] text-[#7fa891]">Facelift</p>
        </div>
        <div className="text-center p-2 bg-[#0d1310] border border-[#1a2a20]">
          <p className="text-[6px] text-[#7fa891]">Blepharoplasty</p>
        </div>
      </div>
    </div>
  </div>
);

// STYLE 3: Dark Purple & Champagne (Aesthetic Clinic - Stockholm inspired)
const MockupClinicStockholm = () => (
  <div className="w-full h-full bg-[#0d0a10] p-4 overflow-hidden">
    <div className="flex items-center gap-2 mb-3">
      <div className="flex gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-[#1a1520]"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-[#1a1520]"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-[#1a1520]"></div>
      </div>
      <div className="flex-1 bg-white/5 rounded h-5 px-3 flex items-center">
        <span className="text-[8px] text-white/30">estetik-stockholm.se</span>
      </div>
    </div>
    <div className="bg-[#08060a] rounded overflow-hidden h-[calc(100%-32px)] border border-[#1a1520]">
      <div className="px-4 py-2 flex justify-between items-center border-b border-[#1a1520]">
        <span className="text-[10px] tracking-[0.1em] text-[#c9b896]" style={{fontFamily: 'serif'}}>ESTETIK</span>
        <div className="flex gap-4">
          <span className="text-[7px] text-white/30">Behandlingar</span>
          <span className="text-[7px] text-white/30">Om Oss</span>
          <span className="text-[7px] text-[#c9b896]">Boka</span>
        </div>
      </div>
      <div className="px-4 py-6 relative">
        <div className="absolute top-0 right-0 w-32 h-32 bg-[radial-gradient(circle_at_center,rgba(201,184,150,0.06)_0%,transparent_70%)]" />
        <p className="text-[7px] tracking-[0.25em] text-[#8a7a9a] mb-2">PLASTIKKIRURGI</p>
        <p className="text-white/90 text-sm font-light mb-1" style={{fontFamily: 'serif'}}>Skönhet genom</p>
        <p className="text-[#c9b896] text-sm font-light mb-4" style={{fontFamily: 'serif'}}>Precision</p>
        <div className="bg-gradient-to-r from-[#c9b896] to-[#a89876] px-4 py-2 inline-block">
          <span className="text-[7px] text-[#08060a] tracking-[0.15em] font-medium">BOKA KONSULTATION</span>
        </div>
      </div>
      <div className="px-4 pb-4">
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-[#0d0a10] p-2 border border-[#1a1520]">
            <p className="text-[7px] text-white/80">Ansiktslyft</p>
            <p className="text-[5px] text-[#8a7a9a]">Face Lift</p>
          </div>
          <div className="bg-[#0d0a10] p-2 border border-[#1a1520]">
            <p className="text-[7px] text-white/80">Näsplastik</p>
            <p className="text-[5px] text-[#8a7a9a]">Rhinoplasty</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// STYLE 4: Warm Cream & Terracotta (Dental - Mediterranean inspired)
const MockupDentalMediterranean = () => (
  <div className="w-full h-full bg-[#f5f1ec] p-4 overflow-hidden">
    <div className="flex items-center gap-2 mb-3">
      <div className="flex gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-[#c9a07a]"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-[#d4b896]"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-[#a8c4a0]"></div>
      </div>
      <div className="flex-1 bg-white rounded-full h-5 px-3 flex items-center shadow-sm">
        <span className="text-[8px] text-[#8a7a6a]">clinica-sorriso.pt</span>
      </div>
    </div>
    <div className="bg-white rounded-lg overflow-hidden h-[calc(100%-32px)] shadow-sm">
      <div className="px-4 py-2 flex justify-between items-center border-b border-[#f0ebe5]">
        <span className="text-[10px] font-medium text-[#5a4a3a]" style={{fontFamily: 'serif'}}>
          Clínica <span className="text-[#c9876a]">Sorriso</span>
        </span>
        <div className="flex gap-4">
          <span className="text-[7px] text-[#8a7a6a]">Serviços</span>
          <span className="text-[7px] text-[#8a7a6a]">Equipa</span>
          <span className="text-[7px] text-[#c9876a]">Marcar</span>
        </div>
      </div>
      <div className="bg-gradient-to-br from-[#faf7f4] to-[#f5efe8] px-4 py-5">
        <p className="text-[7px] tracking-[0.15em] text-[#c9876a] mb-2">MEDICINA DENTÁRIA</p>
        <p className="text-[#4a3a2a] text-sm font-medium mb-1" style={{fontFamily: 'serif'}}>Sorrisos que</p>
        <p className="text-[#c9876a] text-sm font-medium mb-4" style={{fontFamily: 'serif'}}>Inspiram Confiança</p>
        <div className="bg-[#c9876a] px-4 py-2 rounded-full inline-block">
          <span className="text-[7px] text-white tracking-wider">AGENDAR CONSULTA</span>
        </div>
      </div>
      <div className="p-3 grid grid-cols-3 gap-2">
        <div className="text-center p-2 bg-[#faf7f4] rounded-lg">
          <p className="text-[7px] font-medium text-[#5a4a3a]">Implantes</p>
        </div>
        <div className="text-center p-2 bg-[#faf7f4] rounded-lg">
          <p className="text-[7px] font-medium text-[#5a4a3a]">Ortodontia</p>
        </div>
        <div className="text-center p-2 bg-[#faf7f4] rounded-lg">
          <p className="text-[7px] font-medium text-[#5a4a3a]">Estética</p>
        </div>
      </div>
    </div>
  </div>
);

// STYLE 5: Sage & Cream (Dermatology - Berlin/Minimalist inspired)
const MockupDermaBerlin = () => (
  <div className="w-full h-full bg-[#f8f6f2] p-4 overflow-hidden">
    <div className="flex items-center gap-2 mb-3">
      <div className="flex gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-[#c4d4c0]"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-[#d4dcc8]"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-[#a8c0a0]"></div>
      </div>
      <div className="flex-1 bg-white rounded h-5 px-3 flex items-center shadow-sm">
        <span className="text-[8px] text-[#6a7a6a]">hautarzt-berlin.de</span>
      </div>
    </div>
    <div className="bg-white rounded overflow-hidden h-[calc(100%-32px)] shadow-sm">
      <div className="px-4 py-2 flex justify-between items-center border-b border-[#e8e4dc]">
        <span className="text-[10px] tracking-[0.1em] text-[#4a5a4a]" style={{fontFamily: 'serif'}}>
          HAUT<span className="text-[#8a9a7a]">PRAXIS</span>
        </span>
        <div className="flex gap-4">
          <span className="text-[7px] text-[#6a7a6a]">Leistungen</span>
          <span className="text-[7px] text-[#6a7a6a]">Team</span>
          <span className="text-[7px] text-[#8a9a7a]">Termin</span>
        </div>
      </div>
      <div className="px-4 py-5 bg-gradient-to-b from-white to-[#f8f6f2]">
        <p className="text-[7px] tracking-[0.2em] text-[#8a9a7a] mb-2">DERMATOLOGIE</p>
        <p className="text-[#3a4a3a] text-sm font-medium mb-1" style={{fontFamily: 'serif'}}>Gesunde Haut,</p>
        <p className="text-[#8a9a7a] text-sm font-medium mb-4" style={{fontFamily: 'serif'}}>Natürliche Schönheit</p>
        <div className="border-2 border-[#8a9a7a] px-4 py-2 inline-block hover:bg-[#8a9a7a] hover:text-white transition-colors">
          <span className="text-[7px] text-[#8a9a7a] tracking-wider">TERMIN BUCHEN</span>
        </div>
      </div>
      <div className="px-4 pb-3 grid grid-cols-2 gap-2">
        <div className="bg-[#f4f8f2] p-2 rounded border-l-2 border-[#8a9a7a]">
          <p className="text-[7px] font-medium text-[#4a5a4a]">Akne</p>
          <p className="text-[5px] text-[#6a7a6a]">Behandlung</p>
        </div>
        <div className="bg-[#f4f8f2] p-2 rounded border-l-2 border-[#8a9a7a]">
          <p className="text-[7px] font-medium text-[#4a5a4a]">Laser</p>
          <p className="text-[5px] text-[#6a7a6a]">Therapie</p>
        </div>
      </div>
    </div>
  </div>
);

// STYLE 6: Warm Neutrals (Cardiology - Prague classic inspired)
const MockupCardioPrague = () => (
  <div className="w-full h-full bg-[#f5f2ef] p-4 overflow-hidden">
    <div className="flex items-center gap-2 mb-3">
      <div className="flex gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-[#c9a0a0]"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-[#d4c4b0]"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-[#a8b4a0]"></div>
      </div>
      <div className="flex-1 bg-white rounded h-5 px-3 flex items-center shadow-sm">
        <span className="text-[8px] text-[#7a6a5a]">kardiologie-praha.cz</span>
      </div>
    </div>
    <div className="bg-white rounded overflow-hidden h-[calc(100%-32px)] shadow-sm">
      <div className="px-4 py-2 flex justify-between items-center border-b border-[#e8e2da]">
        <div className="flex items-center gap-1">
          <span className="text-[10px]">❤️</span>
          <span className="text-[10px] font-medium text-[#5a4a3a]" style={{fontFamily: 'serif'}}>CardioVita</span>
        </div>
        <div className="flex gap-4">
          <span className="text-[7px] text-[#8a7a6a]">Vyšetření</span>
          <span className="text-[7px] text-[#8a7a6a]">Tým</span>
          <span className="text-[7px] text-[#a07060]">Objednat</span>
        </div>
      </div>
      <div className="bg-gradient-to-r from-[#faf8f5] to-[#f5f0ea] px-4 py-5">
        <p className="text-[7px] tracking-[0.15em] text-[#a07060] mb-2">KARDIOLOGIE</p>
        <p className="text-[#4a3a2a] text-sm font-medium mb-1" style={{fontFamily: 'serif'}}>Vaše Srdce v</p>
        <p className="text-[#a07060] text-sm font-medium mb-4" style={{fontFamily: 'serif'}}>Nejlepších Rukou</p>
        <div className="flex gap-2">
          <div className="bg-[#a07060] px-3 py-1.5 rounded">
            <span className="text-[7px] text-white tracking-wider">OBJEDNAT SE</span>
          </div>
          <div className="flex items-center gap-1 text-[7px] text-[#7a6a5a] border border-[#d4c4b0] px-2 py-1 rounded">
            <Phone className="w-2 h-2" />
            <span>Linka 24h</span>
          </div>
        </div>
      </div>
      <div className="p-3 grid grid-cols-3 gap-2">
        <div className="text-center bg-[#faf8f5] p-2 rounded">
          <p className="text-[7px] font-medium text-[#5a4a3a]">EKG</p>
        </div>
        <div className="text-center bg-[#faf8f5] p-2 rounded">
          <p className="text-[7px] font-medium text-[#5a4a3a]">Echo</p>
        </div>
        <div className="text-center bg-[#faf8f5] p-2 rounded">
          <p className="text-[7px] font-medium text-[#5a4a3a]">Holter</p>
        </div>
      </div>
    </div>
  </div>
);

const portfolioItems = [
  {
    id: 1,
    title: "Portal Medicina Estética",
    specialty: "Medicina Estética",
    description: "Design noir elegante com acentos rose gold, inspirado em clínicas parisienses.",
    MockupComponent: MockupAestheticParis,
  },
  {
    id: 2,
    title: "Site Cirurgião Plástico",
    specialty: "Cirurgia Plástica",
    description: "Estilo Harley Street com tons esmeralda e prata, transmitindo confiança.",
    MockupComponent: MockupPlasticLondon,
  },
  {
    id: 3,
    title: "Clínica Estética Facial",
    specialty: "Estética Facial",
    description: "Design nórdico minimalista com champagne e púrpura subtil.",
    MockupComponent: MockupClinicStockholm,
  },
  {
    id: 4,
    title: "Website Clínica Dentária",
    specialty: "Dentisteria",
    description: "Tons quentes e acolhedores inspirados no Mediterrâneo.",
    MockupComponent: MockupDentalMediterranean,
  },
  {
    id: 5,
    title: "Clínica Dermatológica",
    specialty: "Dermatologia",
    description: "Estética clean alemã com paleta sage e creme.",
    MockupComponent: MockupDermaBerlin,
  },
  {
    id: 6,
    title: "Centro de Cardiologia",
    specialty: "Cardiologia",
    description: "Design clássico e profissional com tons neutros quentes.",
    MockupComponent: MockupCardioPrague,
  },
];

export const PortfolioCarousel = ({ t }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const nextSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % portfolioItems.length);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating]);

  const prevSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + portfolioItems.length) % portfolioItems.length);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating]);

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [nextSlide, isPaused]);

  const getVisibleItems = () => {
    const items = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % portfolioItems.length;
      items.push(portfolioItems[index]);
    }
    return items;
  };

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="section-dark py-24 md:py-32 relative overflow-hidden"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[radial-gradient(circle_at_center,rgba(197,160,89,0.06)_0%,transparent_60%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <div className="mb-16 md:mb-20">
          <span className="text-sm tracking-[0.3em] uppercase text-[#C5A059] block mb-4">
            {t.portfolio.badge}
          </span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h2
                data-testid="portfolio-title"
                className="text-4xl md:text-5xl font-medium tracking-tight text-[#E5E5E5] max-w-2xl line-accent"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t.portfolio.title}{" "}
                <span className="text-gold-gradient">{t.portfolio.highlight}</span>
              </h2>
              <p className="text-[#A3A3A3] mt-4 max-w-xl">
                {t.portfolio.subtitle}
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={prevSlide}
                data-testid="carousel-prev-btn"
                variant="outline"
                size="icon"
                className="w-12 h-12 border-white/10 text-[#A3A3A3] hover:text-[#C5A059] hover:border-[#C5A059]/30 transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button
                onClick={nextSlide}
                data-testid="carousel-next-btn"
                variant="outline"
                size="icon"
                className="w-12 h-12 border-white/10 text-[#A3A3A3] hover:text-[#C5A059] hover:border-[#C5A059]/30 transition-all"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {getVisibleItems().map((item, idx) => (
              <div
                key={`${item.id}-${idx}`}
                data-testid={`portfolio-card-${idx}`}
                className={`group relative overflow-hidden glass-card transition-all duration-500 ${
                  isAnimating ? "opacity-80 scale-[0.98]" : "opacity-100 scale-100"
                }`}
              >
                <div 
                  className="relative h-72 overflow-hidden"
                  role="img"
                  aria-label={`Exemplo de website médico para ${item.specialty} - ${item.title} - Criação de sites médicos em Portugal`}
                >
                  <item.MockupComponent />
                  <div className="absolute top-6 left-6 z-10">
                    <span className="px-3 py-1 text-xs tracking-widest uppercase bg-[#C5A059] text-[#050505] font-medium">
                      {item.specialty}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3
                    className="text-xl font-medium text-[#E5E5E5] mb-2 group-hover:text-[#C5A059] transition-colors duration-300"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#A3A3A3] leading-relaxed">
                    {item.description}
                  </p>
                </div>

                <div className="absolute inset-0 border border-transparent group-hover:border-[#C5A059]/30 transition-colors duration-300 pointer-events-none" />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-center gap-2 mt-10">
          {portfolioItems.map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                if (!isAnimating) {
                  setIsAnimating(true);
                  setCurrentIndex(idx);
                  setTimeout(() => setIsAnimating(false), 500);
                }
              }}
              data-testid={`carousel-dot-${idx}`}
              className={`w-2 h-2 transition-all duration-300 ${
                idx === currentIndex
                  ? "w-8 bg-[#C5A059]"
                  : "bg-white/20 hover:bg-white/40"
              }`}
            />
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-[#A3A3A3] mb-6">
            {t.portfolio.cta}
          </p>
          <Button
            onClick={() => document.querySelector("#contacto")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="portfolio-cta-btn"
            className="btn-gold bg-[#C5A059] text-[#050505] hover:bg-[#D4B068] px-8 py-6 text-base tracking-widest uppercase font-medium"
          >
            {t.portfolio.ctaBtn}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PortfolioCarousel;
